import type ArrayProxy from '@ember/array/proxy';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import findBy from '@nullvoxpopuli/ember-composable-helpers/helpers/find-by';
import pick from '@nullvoxpopuli/ember-composable-helpers/helpers/pick';
import truncate from 'ember-cli-string-helpers/helpers/truncate';
import type { Task } from 'ember-concurrency';
import { dropTask, restartableTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import sub from 'ember-math-helpers/helpers/sub';
import PowerSelect from 'ember-power-select/components/power-select';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import set from 'ember-set-helper/helpers/set';
import { and, eq, gt, not, notEq, or } from 'ember-truth-helpers';
import type { AnyFlow } from 'garaje/components/sign-in-fields/rules-modal';
import ButtonGroup from 'garaje/components/ui/button-group';
import UiButton from 'garaje/components/ui-button';
import UiInputField from 'garaje/components/ui-input-field';
import UiLabel from 'garaje/components/ui-label';
import UiPill from 'garaje/components/ui-pill';
import type EmployeeModel from 'garaje/models/employee';
import type EmployeeSignInFieldActionsContact from 'garaje/models/employee-sign-in-field-actions-contact';
import type GlobalSignInFieldActionsContact from 'garaje/models/global-sign-in-field-actions-contact';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type SignInFieldActionModel from 'garaje/models/sign-in-field-action';
import type SignInFieldActionRuleGroupModel from 'garaje/models/sign-in-field-action-rule-group';
import type SignInFieldActionsContact from 'garaje/models/sign-in-field-actions-contact';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import type { EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import employeesSearcherTask from 'garaje/utils/employees-searcher';
import { RULE_OPERATION_OPTIONS, RULE_ACTION_OPTIONS, GROUP_OPERATION_OPTIONS } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import LiquidIf from 'liquid-fire/components/liquid-if';
import { alias, filterBy } from 'macro-decorators';

function getOptionsForCondition(condition: SignInFieldActionModel) {
  return condition.signInField.content?.options ?? [];
}

interface ManageRuleSignature {
  Args: {
    /**
     * The flow this new rule will belong
     */
    flow: AnyFlow;
    rule: SignInFieldActionRuleGroupModel;
    cancel: () => void;
    /**
     * Task for saving the rule
     */
    saveRuleTask: Task<void, []>;
    /**
     * Method for invoking the delete confirmation modal
     */
    handleDelete: (rule: SignInFieldActionRuleGroupModel) => void;
    /**
     * Whether we're adding or editing
     */
    isEditing: boolean;
    isLocationAdmin?: boolean;
    isProtect?: boolean;
  };
}

export default class ManageRule extends Component<ManageRuleSignature> {
  elementId = guidFor(this);

  @service declare ajaxFetch: AjaxFetchService;
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;
  @service declare state: StateService;

  @tracked signInFieldActions?: ArrayProxy<SignInFieldActionModel>;
  @tracked signInFieldActionsContacts?: ArrayProxy<SignInFieldActionsContact>;

  get visibleSignInFieldActions(): SignInFieldActionModel[] {
    return (this.signInFieldActions ?? []).filter((action) => !action.isDeleted);
  }

  get errors(): Partial<Record<keyof SignInFieldActionRuleGroupModel, string[]>> {
    const errors: Partial<Record<keyof SignInFieldActionRuleGroupModel, string[]>> = {};
    const actions = this.args.rule.actions;

    if (actions.includes(RULE_ACTION_OPTIONS.REQUIRE_APPROVAL) && actions.includes(RULE_ACTION_OPTIONS.DENY)) {
      if (!errors['actions']) errors['actions'] = [];
      errors['actions'].push('Cannot require entry approval and deny entry at the same time.');
    }

    return errors;
  }

  get hasErrors(): boolean {
    return Object.keys(this.errors).length > 0;
  }

  constructor(owner: unknown, args: ManageRuleSignature['Args']) {
    super(owner, args);

    if (<boolean>(<unknown>this.args.rule.isNew)) {
      this.addCondition();

      // For v1 we are automatically setting this value
      if (this.ruleActionOptions.length === 1) {
        this.args.rule.actions = [RULE_ACTION_OPTIONS.DENY];
      }
    }
    void this.resolveRelationshipsTask.perform();
  }

  /**
   * Operation choices for the dropdown
   */
  ruleOperationOptions = [RULE_OPERATION_OPTIONS.IS];

  /**
   * Action choices for the dropdown
   */
  ruleActionOptions = [RULE_ACTION_OPTIONS.DENY, RULE_ACTION_OPTIONS.ALERT, RULE_ACTION_OPTIONS.REQUIRE_APPROVAL];

  ruleGroupOperatorOptions = [GROUP_OPERATION_OPTIONS.AND, GROUP_OPERATION_OPTIONS.OR];

  /**
   * actions mapped with their id's to more easily map with MultiPowerSelect
   */
  @tracked selectedActions = [];

  @alias('args.flow.signInFieldPage.signInFields.content') signInFields: SignInFieldModel[] | undefined;

  @filterBy('singleSelectionFields', 'isNew', false) availableFields!: SignInFieldModel[];

  /**
   * list of fields
   */
  get singleSelectionFields(): SignInFieldModel[] {
    // Want to filter out host, email, phone, etc. for v1
    return this.signInFields?.filter((field) => !field.isHost && !!field.isSingleSelection) ?? [];
  }

  /**
   * list of active employee ids to cross reference against the search
   */
  get activeContactIds(): Array<string | undefined> {
    return (
      this.signInFieldActionsContacts
        ?.filter((contact) => !contact.isDeleted)
        .map((contact) => contact.employee.content?.id) ?? []
    );
  }

  /**
   * determines if form is valid
   */
  get canSaveRule(): boolean {
    const { actions } = this.args.rule;
    const { visibleSignInFieldActions } = this;
    if (!visibleSignInFieldActions?.length) {
      return false;
    }

    if (!actions.length) {
      return false;
    }

    if (actions.includes(RULE_ACTION_OPTIONS.ALERT) && !this.activeContactIds.length) {
      return false;
    }

    if (this.hasErrors) {
      return false;
    }

    return visibleSignInFieldActions.every((condition) => {
      return condition.signInField.content?.isValid && !!condition.matchOperator && !!condition.signInFieldValue;
    });
  }

  /**
   * determines if can delete extra conditions
   */
  get canDeleteConditions(): boolean {
    return <number>this.signInFieldActions?.length > 1;
  }

  /**
   * the contacts showing in the multiselect-dropdown
   */
  get selectedEmployees(): EmployeeModel[] {
    const contacts = this.signInFieldActionsContacts?.filter((contact) => !contact.isDeleted) ?? [];
    return contacts.map((contact) => contact.employee.content!);
  }

  @action
  addCondition(): void {
    const actionType = this.args.flow.isGlobal
      ? 'global-sign-in-field-action'
      : this.args.flow.isProtect
        ? 'employee-sign-in-field-action'
        : 'sign-in-field-action';
    this.store.createRecord(actionType, {
      action: RULE_ACTION_OPTIONS.DENY,
      flow: this.args.flow,
      matchOperator: RULE_OPERATION_OPTIONS.IS,
      signInFieldActionRuleGroup: this.args.rule,
    });
  }

  @action
  deleteCondition(condition: SignInFieldActionModel): void {
    if (this.canDeleteConditions) {
      condition.deleteRecord();
    }

    if (this.visibleSignInFieldActions.length === 1) {
      this.args.rule.groupOperator = GROUP_OPERATION_OPTIONS.AND;
    }
  }

  /**
   * @param selectedEmployee employee selected from dropdown
   * @returns Converts an employee to a `contact`. Returns null if removing
   * This result of this function is passed to `setAlertContacts` via ember-power-select's onChange
   */
  @action
  handleBuildSelection(
    selectedEmployee: EmployeeModel,
  ): SignInFieldActionsContact | GlobalSignInFieldActionsContact | EmployeeSignInFieldActionsContact | null {
    const contactToRemove = this.signInFieldActionsContacts?.find(
      (contact) => contact.employee.content?.id === selectedEmployee.id,
    );

    if (contactToRemove) {
      // the user is adding back a person they previously removed, prior to saving
      if (<boolean>(<unknown>contactToRemove.isDeleted) && !contactToRemove.isNew) {
        contactToRemove.rollbackAttributes();
        return null;
      }

      contactToRemove.deleteRecord();
      return null;
    }

    const modelType = this.#actionContactModelType();
    return this.store.createRecord(modelType, {
      employee: selectedEmployee,
      actionType: RULE_ACTION_OPTIONS.ALERT,
      signInFieldActionRuleGroup: this.args.rule,
    });
  }

  #actionContactModelType() {
    if (this.args.isProtect) {
      return 'employee-sign-in-field-actions-contact';
    } else if (this.args.flow.isGlobal) {
      return 'global-sign-in-field-actions-contact';
    }

    return 'sign-in-field-actions-contact';
  }

  @action
  handleKeydown(_dropdown: unknown, ev: KeyboardEvent): void {
    if (ev.code === 'Enter') {
      ev.preventDefault();
    }
  }

  @action
  async setAlertContacts(newContact: SignInFieldActionsContact): Promise<void> {
    if (!newContact) {
      return;
    }

    const contacts = await this.args.rule.signInFieldActionsContacts;
    contacts.pushObject(newContact);
  }

  searchContactsTask = restartableTask(async (term: string) => {
    const results = await this.searchEmployeesTask.perform(
      term,
      {},
      { withoutLocation: this.args.flow.isGlobal || this.args.flow.isProtect },
    );

    return results.filter(({ id }) => !this.activeContactIds.includes(id));
  });

  @(employeesSearcherTask({
    filter: { deleted: false },
    prefix: true,
  }).restartable())
  searchEmployeesTask!: EmployeeSearcherTask;

  /**
   * sends a test email to the current user
   */
  sendTestEmailAlertTask = dropTask(async () => {
    const url = urlBuilder.v3.sendTestNotificationUrl();
    const { currentLocation } = this.state;

    try {
      await this.ajaxFetch.request(url, {
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
          data: {
            location_id: currentLocation.id,
          },
        }),
      });
      this.flashMessages.showAndHideFlash('success', 'Sent!');
    } catch (err) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(err));
    }
  });

  resolveRelationshipsTask = restartableTask(async () => {
    const [signInFieldActions, signInFieldActionsContacts] = await Promise.all([
      this.args.rule.signInFieldActions,
      this.args.rule.signInFieldActionsContacts,
    ]);

    this.signInFieldActions = signInFieldActions;
    this.signInFieldActionsContacts = signInFieldActionsContacts;
  });

  @action
  willDestroy(): void {
    super.willDestroy();
    if (this.args.rule && !this.args.rule?.isDestroying) {
      this.signInFieldActions?.forEach((condition) => condition.rollbackAttributes());
      this.args.rule.rollbackAttributes();
    }
  }

  <template>
    <div class="flex flex-col items-left manage-rule" data-test-rules-modal-manage>
      <div class="flex justify-between items-center">
        <div class="flex flex-col">
          <h4>Set triggers</h4>
          <h4 class="text-carbon-40 font-normal">
            Action will be triggered only when all conditions are met.
          </h4>
        </div>
        {{#if (gt this.visibleSignInFieldActions.length 1)}}
          <UiInputField data-test-rule-condition-group-operator-select class="w-16 min-w-0 min-h-0" as |Field|>
            <Field.label>Operator</Field.label>
            <PowerSelect
              @renderInPlace={{false}}
              @options={{this.ruleGroupOperatorOptions}}
              @onChange={{set @rule "groupOperator"}}
              @selected={{@rule.groupOperator}}
              @placeholder="Operator"
              @dropdownClass="in-modal"
              @required={{true}}
              @disabled={{and @isProtect @isLocationAdmin}}
              @triggerClass="powerSelect-small"
              as |option|
            >
              <span class="uppercase">{{option}}</span>
            </PowerSelect>
          </UiInputField>
        {{/if}}
      </div>

      <form {{on "submit" (perform @saveRuleTask)}}>
        <div class="mt-4">
          {{#each this.visibleSignInFieldActions as |condition idx|}}
            <div class="flex items-center h-10">
              <div class="w-4">If</div>
              <div data-test-rules-modal-manage-conditional={{idx}} style="width: 14.5rem;">
                <PowerSelect
                  @renderInPlace={{false}}
                  @options={{this.availableFields}}
                  @onChange={{set condition "signInField"}}
                  @selected={{condition.signInField}}
                  @placeholder="Sign-in field"
                  @dropdownClass="in-modal"
                  @required={{true}}
                  @disabled={{and @isProtect @isLocationAdmin}}
                  as |option|
                >
                  {{truncate option.name 100 true}}
                </PowerSelect>
              </div>
              <div class="mx-1 w-16" data-test-rules-modal-manage-operation={{idx}}>
                <PowerSelect
                  @renderInPlace={{true}}
                  @options={{this.ruleOperationOptions}}
                  @onChange={{set condition "matchOperator"}}
                  @selected={{condition.matchOperator}}
                  @required={{true}}
                  @disabled={{or (eq this.ruleOperationOptions.length 1) (and @isProtect @isLocationAdmin)}}
                  as |option|
                >
                  {{option}}
                </PowerSelect>
              </div>
              <div data-test-rules-modal-manage-option={{idx}} style="width: 14.5rem;">
                <PowerSelect
                  @renderInPlace={{false}}
                  @options={{getOptionsForCondition condition}}
                  @onChange={{pick "id" (set condition "signInFieldValue")}}
                  @selected={{findBy "id" condition.signInFieldValue (getOptionsForCondition condition)}}
                  @placeholder="Option"
                  @dropdownClass="in-modal"
                  @required={{true}}
                  @disabled={{and @isProtect @isLocationAdmin}}
                  as |option|
                >
                  {{option.value}}
                </PowerSelect>
              </div>
              {{#if (and this.canDeleteConditions (not (and @isProtect @isLocationAdmin)))}}
                <UiButton
                  data-test-rules-modal-manage-delete-condition
                  class="btn btn--smaller btn--square btn--hoverFill ml-1"
                  {{on "click" (fn this.deleteCondition condition)}}
                  tabindex="-1"
                >
                  <span class="icon-trash"></span>
                </UiButton>
              {{/if}}
            </div>
            {{#if (notEq idx (sub this.visibleSignInFieldActions.length 1))}}
              <div class="border-l border-carbon-20 h-20 -my-6 absolute" style="left: 4rem; z-index: -1;" />
              <UiPill @fill="grayLighter" class="uppercase ml-4 my-2" data-test-rule-condition-logic-operator>
                {{@rule.groupOperator}}
              </UiPill>
            {{/if}}
          {{/each}}
        </div>

        {{#if (not (and @isProtect @isLocationAdmin))}}
          <UiButton
            {{on "click" this.addCondition}}
            class="btn btn--link !text-gem-50 !text-left ml-4 font-normal add-condition mt-4"
            data-test-rules-modal-manage-add-condition
          >
            Add another trigger
          </UiButton>
        {{/if}}

        <div class="mt-10">
          <h4>Define actions</h4>
          <h4 class="text-carbon-40 font-normal">
            Notify admins and/or determine entry eligibility based on your triggers.
          </h4>

          <div class="mb-4">
            <div class="flex items-center mt-4 mb-6">
              <span>Then</span>

              <UiInputField
                @errors={{this.errors.actions}}
                class="flex-1 ml-2 h-10"
                data-test-rules-modal-manage-actions
              >
                <div class="mb-5">
                  <PowerSelectMultiple
                    @triggerClass={{if this.errors.actions.length "power-select-error"}}
                    @searchEnabled={{false}}
                    @renderInPlace={{false}}
                    @options={{this.ruleActionOptions}}
                    @onChange={{set @rule "actions"}}
                    @selected={{@rule.actions}}
                    @placeholder="Choose actions"
                    @disabled={{or (eq this.ruleActionOptions.length 1) (and @isProtect @isLocationAdmin)}}
                    @dropdownClass="in-modal"
                    @required={{true}}
                    as |option|
                  >
                    {{#if (eq option "deny_entry")}}
                      Deny entry
                    {{else if (eq option "alert")}}
                      Notify
                    {{else}}
                      Require entry approval
                    {{/if}}
                  </PowerSelectMultiple>
                </div>
              </UiInputField>
            </div>

            <LiquidIf @predicate={{@rule.hasAlertAction}}>
              <div class="rules-configure-alerts bg-carbon-5 p-4 rounded">
                <div class="flex justify-between mb-4">
                  <h5>Configure notifications</h5>
                  <UiButton
                    data-test-send-test-email
                    class="btn btn--link btn--small !text-gem-50"
                    {{on "click" (perform this.sendTestEmailAlertTask)}}
                  >
                    {{if this.sendTestEmailAlertTask.isRunning "Sending..." "Send test to me"}}
                  </UiButton>
                </div>
                <div data-test-manage-rule-alert-contact>
                  <UiLabel
                    @required={{true}}
                    @for={{concat this.elementId "contacts"}}
                    @text="Select contacts to notify"
                  />
                  <PowerSelectMultiple
                    @triggerId={{concat this.elementId "contacts"}}
                    @ariaDescribedBy="alert-contacts"
                    @required={{true}}
                    @searchEnabled={{true}}
                    @search={{perform this.searchContactsTask}}
                    @searchField="name"
                    @dropdownClass="in-modal"
                    @buildSelection={{this.handleBuildSelection}}
                    @onChange={{this.setAlertContacts}}
                    @onKeydown={{this.handleKeydown}}
                    @selected={{this.selectedEmployees}}
                    @placeholder="Choose contacts"
                    as |option|
                  >
                    {{option.name}}
                  </PowerSelectMultiple>
                </div>
                {{#if @flow.isGlobal}}
                  <div class="text-sm text-carbon-40 mt-4">
                    Contacts will only receive notifications if they are included in the employee directory at that
                    location. Learn more about
                    <a
                      data-test-plans-contact-us
                      href="https://envoy.help/en/articles/4843829-setting-up-global-sign-in-flows#h_88b4f5bf0f"
                    >rule-based notifications</a>.
                  </div>
                {{/if}}
              </div>
            </LiquidIf>
          </div>
        </div>
        <div class="flex justify-end mt-8">
          {{#if (and @isEditing (not (and @isProtect @isLocationAdmin)))}}
            <div class="flex-1">
              <UiButton
                data-test-rules-modal-delete
                class="btn btn--small btn--nonHoverFill"
                {{on "click" (fn @handleDelete @rule)}}
              >
                <span class="icon-trash" />
                <span class="ml-1">Delete rule</span>
              </UiButton>
            </div>
          {{/if}}
          <ButtonGroup>
            <UiButton data-test-rules-modal-cancel {{on "click" @cancel}} class="btn btn--hoverFill btn--small">
              Cancel
            </UiButton>
            <UiButton
              @type="submit"
              class="btn btn--brand btn--small"
              disabled={{or (not this.canSaveRule) @saveRuleTask.isRunning}}
              data-test-rules-modal-save
            >
              Save rule
            </UiButton>
          </ButtonGroup>
        </div>
      </form>
    </div>
  </template>
}
